import { reduxKey } from './constants';
import { createAction } from 'redux-actions';
import { createCallbackActionCreator } from '@wix/da-shared-react/pkg/Stream/helpers';
import {
  ToolType,
  TabType,
  TopTabType,
  DreamupInpaintResponse,
  DreamupResultsResponse,
  AIAspectRatio,
  MiniToolType,
  MicroToolType,
  AdjustType,
  CoreStatus,
  InterfaceType,
  MobileType,
  BackgroundRemovalResponse,
  BackgroundResultsResponse,
} from '../types';
import { BrushName } from '@wix/da-muro-core/types/UITypes';
import { ColorType, DreamupStyle } from '@wix/da-muro-core/types/muro';
import { PapiDeviation } from '@wix/da-papi-types';

export const stateUpdated = createAction(`${reduxKey}.STATE_UPDATED`, (updated: any) => ({
  updated,
}));

// Dynamic redux
export const reduxModuleLoaded = createAction(`${reduxKey}.REDUX_READY`, () => ({}));

export const muroReady = createAction(`${reduxKey}.MURO_READY`, () => ({}));

export const showGenericAlert = createAction(`${reduxKey}.SHOW_ALERT`, config => ({
  ...config,
}));

export const closeGenericAlert = createAction(`${reduxKey}.CLOSE_ALERT`, () => ({}));

export const showTerseAlert = createAction(`${reduxKey}.SHOW_TERSE_ALERT`, config => ({
  ...config,
}));

export const closeTerseAlert = createAction(`${reduxKey}.CLOSE_TERSE_ALERT`, () => ({}));

export const showGenericConfirm = createAction(
  `${reduxKey}.SHOW_CONFIRM`,
  (config, promiseResolver) => ({ config, promiseResolver })
);

export const closeGenericConfirm = createAction(`${reduxKey}.CLOSE_CONFIRM`, () => ({}));

export const showDeleteImageConfirm = createAction(
  `${reduxKey}.SHOW_DELETE_IMAGE_CONFIRM`,
  ({ deviationId }: { deviationId: number }) => ({
    deviationId,
  })
);

export const closeDeleteImageConfirm = createAction(
  `${reduxKey}.CLOSE_DELETE_IMAGE_CONFIRM`,
  () => ({})
);

export const showFilenameModal = createAction(
  `${reduxKey}.SHOW_FILENAME_MODAL`,
  promiseResolver => ({ promiseResolver })
);

export const closeFilenameModal = createAction(`${reduxKey}.CLOSE_FILENAME_MODAL`, () => ({}));

export const showNewfileModal = createAction(`${reduxKey}.SHOW_NEWFILE_MODAL`, promiseResolver => ({
  promiseResolver,
}));

export const closeNewfileModal = createAction(`${reduxKey}.CLOSE_NEWFILE_MODAL`, () => ({}));

export const showPressureModal = createAction(
  `${reduxKey}.SHOW_PRESSURE_MODAL`,
  promiseResolver => ({ promiseResolver })
);

export const closePressureModal = createAction(`${reduxKey}.CLOSE_PRESSURE_MODAL`, () => ({}));

export const showMediaSelector = createAction(
  `${reduxKey}.SHOW_MM`,
  ({ mmConfig, promiseResolver }) => ({ mmConfig, promiseResolver })
);

export const initToolBrushDefault = createAction(`${reduxKey}.INIT_TOOL_BRUSH`, () => ({}));

export const storeActiveBrush = createAction(
  `${reduxKey}.STORE_BRUSH`,
  ({ tool, brushName }: { tool: ToolType; brushName: BrushName }) => ({
    tool,
    brushName,
  })
);

export const updateStoredBrushes = createAction(
  `${reduxKey}.UPDATE_BRUSH_STORE`,
  ({ tool, brushName }: { tool: ToolType; brushName: BrushName }) => ({
    tool,
    brushName,
  })
);

export const loadStoredBrush = createAction(
  `${reduxKey}.LOAD_BRUSH_STORE`,
  (tool, currentBrush) => ({
    tool,
    currentBrush,
  })
);

export const backToDraw = createAction(`${reduxKey}.MICRO_BACK_TO_DRAW`, () => ({}));

export const backToErase = createAction(`${reduxKey}.BACK_TO_ERASE`, () => ({}));

export const backToSelect = createAction(`${reduxKey}.BACK_TO_SELECT`, () => ({}));

export const backToTransform = createAction(`${reduxKey}.BACK_TO_TRANSFORM`, () => ({}));

export const updateTopTab = createAction(
  `${reduxKey}.UPDATE_TOP_TAB`,
  ({ topTab }: { topTab: TopTabType }) => ({
    topTab,
  })
);

export const updateTab = createAction(`${reduxKey}.UPDATE_TAB`, ({ tab }: { tab: TabType }) => ({
  tab,
}));

export const updateTool = createAction(
  `${reduxKey}.UPDATE_TOOL`,
  ({ tool }: { tool: ToolType }) => ({
    tool,
  })
);

export const updateAdjust = createAction(
  `${reduxKey}.UPDATE_ADJUST`,
  ({ adjust }: { adjust: AdjustType }) => ({
    adjust,
  })
);

export const updateColorPickerColor = createAction(
  `${reduxKey}.UPDATE_COLOR_PICKER_COLOR`,
  ({ colorPickerColor }: { colorPickerColor: ColorType }) => ({
    colorPickerColor,
  })
);

export const updateInterfaceType = createAction(
  `${reduxKey}.UPDATE_INTERFACE_TYPE`,
  ({ interfaceType }: { interfaceType: InterfaceType }) => ({
    interfaceType,
  })
);

export const updateIsInitialized = createAction(
  `${reduxKey}.UPDATE_IS_INITIALIZED`,
  ({ isInitialized }: { isInitialized: boolean }) => ({
    isInitialized,
  })
);

export const updateLayerFlyoutOpen = createAction(
  `${reduxKey}.UPDATE_LAYER_FLYOUT`,
  ({ layerFlyoutOpen }: { layerFlyoutOpen: boolean }) => ({
    layerFlyoutOpen,
  })
);

export const updateDeviationId = createAction(
  `${reduxKey}.UPDATE_DEVIATION_ID`,
  ({ deviationId }: { deviationId: number }) => ({
    deviationId,
  })
);

export const updateContentVersion = createAction(
  `${reduxKey}.UPDATE_CONTENT_VERSION`,
  ({ contentVersion }: { contentVersion: string }) => ({
    contentVersion,
  })
);

export const updateFileSaveState = createAction(
  `${reduxKey}.UPDATE_FILE_SAVE_STATE`,
  ({ fileSaveState }: { fileSaveState: 'idle' | 'inProgress' }) => ({
    fileSaveState,
  })
);

export const updateFileLoadState = createAction(
  `${reduxKey}.UPDATE_FILE_LOAD_STATE`,
  ({ fileLoadState }: { fileLoadState: 'idle' | 'inProgress' }) => ({
    fileLoadState,
  })
);

export const createFileSaveRequest = createAction(
  `${reduxKey}.CREATE_FILE_SAVE_REQUEST `,
  (payload: {
    deviationid: number;
    content: string;
    content_version: string;
    uploaded_file_layerids: string[];
    layer_files: File[];
    successFunc: (result: any) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createDeviationLoadRequest = createAction(
  `${reduxKey}.CREATE_DEVIATION_LOAD_REQUEST`,
  (payload: {
    deviationid: number;
    successFunc: (result: any) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createDeviationCreate = createAction(
  `${reduxKey}.CREATE_DEVIATION_CREATE `,
  (payload: {
    pngFile: File;
    didUseAI: boolean;
    isComment: boolean;
    filename?: string;
    successFunc: (result: any) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createDeviationImageRequest = createAction(
  `${reduxKey}.CREATE_DEVIATION_IMAGE_REQUEST `,
  (payload: {
    deviationid: number;
    originalImage: File;
    successFunc: (result: any) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createDeviationFetchRequest = createAction(
  `${reduxKey}.CREATE_DEVIATION_FETCH_REQUEST `,
  (payload: {
    deviationid: number;
    successFunc: (deviation: PapiDeviation) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createDeviationDeleteRequest = createAction(
  `${reduxKey}.CREATE_DEVIATION_DELETE_REQUEST `,
  (payload: { deviationid: number }) => payload
);

export const updateGenerationId = createAction(
  `${reduxKey}.UPDATE_GENERATION_ID`,
  ({ generationId }: { generationId: number }) => ({
    generationId,
  })
);

export const inpaintGenerationSucceeded = createAction(
  `${reduxKey}.INPAINT_GENERATION_SUCCEEDED`,
  ({ generationId }: { generationId: number }) => ({
    generationId,
  })
);

export const inpaintGenerationFailed = createAction(
  `${reduxKey}.INPAINT_GENERATION_FAILED`,
  (generationId: number, errorCode: number | undefined) => ({
    generationId,
    errorCode,
  })
);

export const backgroundRemovalSucceeded = createAction(
  `${reduxKey}.BACKGROUND_REMOVAL_SUCCEEDED`,
  ({ generationId }: { generationId: number }) => ({
    generationId,
  })
);

export const backgroundRemovalFailed = createAction(
  `${reduxKey}.BACKGROUND_REMOVAL_FAILED`,
  (generationId: number, errorCode: number | undefined) => ({
    generationId,
    errorCode,
  })
);

export const updateDreamupRequestState = createAction(
  `${reduxKey}.UPDATE_DREAMUP_REQUEST_STATE`,
  ({ requestState }: { requestState: 'idle' | 'inProgress' }) => ({
    requestState,
  })
);

export const updateBackgroundGenerationId = createAction(
  `${reduxKey}.UPDATE_BACKGROUND_GENERATION_ID`,
  ({ generationId }: { generationId: number }) => ({
    generationId,
  })
);

export const updateBackgroundRequestState = createAction(
  `${reduxKey}.UPDATE_BACKGROUND_REQUEST_STATE`,
  ({ requestState }: { requestState: 'idle' | 'inProgress' }) => ({
    requestState,
  })
);

export const setDreamupFetchState = createAction(
  `${reduxKey}.SET_DREAMUP_FETCH_STATE `,
  (payload: { fetchedRestrictions: boolean }) => payload
);

export const storeDreamupRestrictions = createAction(
  `${reduxKey}.STORE_DREAMUP_RESTRICTIONS `,
  (payload: {
    canUseFreePrompt: boolean;
    canUsePaidPrompt: boolean;
    freePromptsAllowed: number;
    freePromptsRemaining: number;
    imagesPerPrompt: number;
    promptPointsPrice: number;
    pointsBalance: number;
    queuePriority: string;
  }) => payload
);

export const createInpaintRequest = createAction(
  `${reduxKey}.CREATE_INPAINT_REQUEST `,
  (payload: {
    parent_deviationid: number;
    prompt: string;
    negative_prompt: string;
    use_points: boolean;
    guidance_scale: number;
    style: DreamupStyle;
    base_image_file: File;
    mask_image_file: File;
    successFunc: (result: DreamupInpaintResponse) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createSketchGuidedRequest = createAction(
  `${reduxKey}.CREATE_SKETCH_GUIDED_REQUEST `,
  (payload: {
    parent_deviationid: number;
    prompt: string;
    negative_prompt: string;
    use_points: boolean;
    guidance_scale: number;
    style: DreamupStyle;
    sketch_image_file: File;
    successFunc: (result: DreamupInpaintResponse) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createSyncDreamup = createAction(
  `${reduxKey}.CREATE_SYNC_DREAMUP `,
  (payload: {
    prompt: string;
    username: string;
    successFunc: (blob: string) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createCanvasRequest = createAction(
  `${reduxKey}.CREATE_CANVA_REQUEST `,
  (payload: {
    parent_deviationid: number;
    prompt: string;
    negative_prompt: string;
    use_points: boolean;
    guidance_scale: number;
    style: DreamupStyle;
    width: number;
    height: number;
    aspect_ratio: AIAspectRatio;
    successFunc: (result: DreamupInpaintResponse) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createDreamupResultsRequest = createAction(
  `${reduxKey}.CREATE_DREAMUP_RESULTS_REQUEST `,
  (payload: {
    generationid: number;
    successFunc: (result: DreamupResultsResponse) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createBackgroundResultsRequest = createAction(
  `${reduxKey}.CREATE_BACKGROUND_RESULTS_REQUEST `,
  (payload: {
    generationid: number;
    successFunc: (result: BackgroundResultsResponse) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const createBackgroundRemovalRequest = createAction(
  `${reduxKey}.CREATE_BACKGROUND_REMOVAL_REQUEST `,
  (payload: {
    parent_deviationid: number;
    use_points: boolean;
    base_image_file: File;
    successFunc: (result: BackgroundRemovalResponse) => void;
    errorFunc: (e: Error) => void;
  }) => payload
);

export const updateBrushFlyout = createAction(
  `${reduxKey}.UPDATE_BRUSH_FLYOUT`,
  ({ brushFlyoutOpen }: { brushFlyoutOpen: boolean }) => ({
    brushFlyoutOpen,
  })
);

export const updateEraseFlyout = createAction(
  `${reduxKey}.UPDATE_ERASE_FLYOUT`,
  ({ eraseFlyoutOpen }: { eraseFlyoutOpen: boolean }) => ({
    eraseFlyoutOpen,
  })
);

export const updateSelectFlyout = createAction(
  `${reduxKey}.UPDATE_SELECT_FLYOUT`,
  ({ selectFlyoutOpen }: { selectFlyoutOpen: boolean }) => ({
    selectFlyoutOpen,
  })
);

export const updateTransformFlyout = createAction(
  `${reduxKey}.UPDATE_TRANSFORM_FLYOUT`,
  ({ transformFlyoutOpen }: { transformFlyoutOpen: boolean }) => ({
    transformFlyoutOpen,
  })
);

export const updateTextFlyout = createAction(
  `${reduxKey}.UPDATE_TEXT_FLYOUT`,
  ({ textFlyoutOpen }: { textFlyoutOpen: boolean }) => ({
    textFlyoutOpen,
  })
);

export const updateMiniTool = createAction(
  `${reduxKey}.UPDATE_MINI_TOOL`,
  ({ miniTool }: { miniTool: MiniToolType }) => ({
    miniTool,
  })
);
export const updateShowUI = createAction(
  `${reduxKey}.UPDATE_SHOW_UI`,
  ({ showUI }: { showUI: boolean }) => ({
    showUI,
  })
);
export const toggleShowUI = createAction(`${reduxKey}.TOGGLE_SHOW_UI`, () => ({}));

export const updateShowLayers = createAction(
  `${reduxKey}.UPDATE_SHOW_LAYERS`,
  ({ showLayers }: { showLayers: boolean }) => ({
    showLayers,
  })
);

export const updateShowLHPalettes = createAction(
  `${reduxKey}.UPDATE_SHOW_LH_PALETTES`,
  ({ showLHPalettes }: { showLHPalettes: boolean }) => ({
    showLHPalettes,
  })
);

export const updateShowBrushPicker = createAction(
  `${reduxKey}.UPDATE_SHOW_BRUSH_PICKER`,
  ({ showBrushPicker }: { showBrushPicker: boolean }) => ({
    showBrushPicker,
  })
);

export const updateShowImportMediaManager = createAction(
  `${reduxKey}.UPDATE_SHOW_IMPORT_MEDIA_MANAGER`,
  ({ showImportMediaManager }: { showImportMediaManager: boolean }) => ({
    showImportMediaManager,
  })
);

export const updateTriggerSave = createAction(
  `${reduxKey}.UPDATE_TRIGGER_SAVE`,
  ({ triggerSave }: { triggerSave: boolean }) => ({
    triggerSave,
  })
);

export const updateTriggerSaveAs = createAction(
  `${reduxKey}.UPDATE_TRIGGER_SAVE_AS`,
  ({ triggerSaveAs }: { triggerSaveAs: boolean }) => ({
    triggerSaveAs,
  })
);

export const updateMicroTool = createAction(
  `${reduxKey}.UPDATE_MICRO_TOOL`,
  ({ microTool }: { microTool: MicroToolType }) => ({
    microTool,
  })
);

export const updateMaxCapacity = createAction(
  `${reduxKey}.UPDATE_MAX_CAPACITY`,
  ({ maxCapacity }: { maxCapacity: number }) => ({
    maxCapacity,
  })
);

export const updateFreeCapacity = createAction(
  `${reduxKey}.UPDATE_FREE_CAPACITY`,
  ({ freeCapacity }: { freeCapacity: number }) => ({
    freeCapacity,
  })
);

export const updateStashFolderId = createAction(
  `${reduxKey}.UPDATE_STASH_FOLDER_ID`,
  ({ stashFolderId }: { stashFolderId: number }) => ({
    stashFolderId,
  })
);

export const updateLibraryWasInitialized = createAction(
  `${reduxKey}.UPDATE_LIBRARY_INITIALIZED`,
  ({ wasInitialized }: { wasInitialized: boolean }) => ({
    wasInitialized,
  })
);

export const updateHasEmptyPromptError = createAction(
  `${reduxKey}.UPDATE_HAS_EMPTY_PROMPT_ERROR`,
  ({ hasEmptyPromptError }: { hasEmptyPromptError: boolean }) => ({
    hasEmptyPromptError,
  })
);

export const initializeMyLibrary = createAction(
  `${reduxKey}.INITIALIZE_MY_LIBRARY`,
  (deviations: PapiDeviation[]) => ({ deviations })
);

export const fetchNextMyLibrary = createCallbackActionCreator(`${reduxKey}.FETCH_NEXT_MY_LIBRARY`);

export const insertDeviationIntoLibrary = createAction(
  `${reduxKey}.INSERT_DEVIATION_MY_LIBRARY`,
  ({ deviation }: { deviation: PapiDeviation }) => ({
    deviation,
  })
);

export const updateUsername = createAction(
  `${reduxKey}.UPDATE_USERNAME`,
  ({ username }: { username: string }) => ({
    username,
  })
);

export const updateCoreStatus = createAction(
  `${reduxKey}.UPDATE_CORE_STATUS`,
  ({ coreStatus }: { coreStatus: CoreStatus }) => ({
    coreStatus,
  })
);

export const initializeCore = createAction(`${reduxKey}.INITIALIZE_CORE`, () => ({}));

// Mobile Interface Actions
export const updateShowColorPalette = createAction(
  `${reduxKey}.UPDATE_SHOW_COLOR_PALETTE`,
  ({ showColorPalette }: { showColorPalette: boolean }) => ({
    showColorPalette,
  })
);

export const updateShowMobileBrushPicker = createAction(
  `${reduxKey}.UPDATE_SHOW_MOBILE_BRUSH_PICKER`,
  ({ showBrushPicker }: { showBrushPicker: boolean }) => ({
    showBrushPicker,
  })
);

export const updateShowTextPalette = createAction(
  `${reduxKey}.UPDATE_SHOW_TEXT_PALETTE`,
  ({ showTextPalette }: { showTextPalette: boolean }) => ({
    showTextPalette,
  })
);

export const updateShowApplyCoverup = createAction(
  `${reduxKey}.UPDATE_SHOW_APPLY_COVERUP`,
  ({ showApplyCoverup }: { showApplyCoverup: boolean }) => ({
    showApplyCoverup,
  })
);

export const updateMobileType = createAction(
  `${reduxKey}.UPDATE_MOBILE_TYPE`,
  ({ mobileType }: { mobileType: MobileType }) => ({
    mobileType,
  })
);

export const updateShowVariationSelector = createAction(
  `${reduxKey}.UPDATE_SHOW_VARIATION_SELECTOR`,
  ({ showVariationSelector }: { showVariationSelector: boolean }) => ({
    showVariationSelector,
  })
);

export const updateVariationUrls = createAction(
  `${reduxKey}.UPDATE_VARIATION_URLS`,
  ({ variationUrls }: { variationUrls: string[] }) => ({
    variationUrls,
  })
);

export const updateSelectedVariationUrl = createAction(
  `${reduxKey}.UPDATE_SELECTED_VARIATION_URL`,
  ({ selectedVariationUrl }: { selectedVariationUrl: string | null }) => ({
    selectedVariationUrl,
  })
);

export const updateSelectedVariation = createAction(
  `${reduxKey}.UPDATE_SELECTED_VARIATION`,
  ({ selectedVariation }: { selectedVariation: number }) => ({
    selectedVariation,
  })
);
